import { Fragment, h } from 'preact';
import {
  decodeId,
  generateUrl,
  ProductPolicy,
  setProductCartLabel,
  validateEmail,
} from 'utils/helpers';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'preact/hooks';

import middleware from 'utils/middleware';

import clsx from 'clsx';
import { formatMoney } from 'utils/storefront/format-resource';
import useInView from 'utils/in-view';

import useUniqueId from 'utils/unique-id';
import useResponsive from 'utils/responsive';
import ProductPrice from './product-price';
import ProductStickyButton from './product-sticky-button';
import ProductSwatches from '../product-swatches/product-swatches';
import VariantSelector from './variant-selector';
import ProductStyleNumber from './product-style-number';
import ProductPolicyNotes from './product-policy-notes';
import ProductReturnPolicyNotes from './product-return-policy-notes';
import useDiscount from '../product-discounts/product-discount';
import productBrRecommendations from './product-br-recommendations';

// Helper function to show date error messages
const showDateError = (message) => {
  const dateContainer = document.getElementById('date-container');
  if (dateContainer) {
    const input = dateContainer.querySelector('.field__input');
    const dateError = document.createElement('div');
    dateError.id = 'send-on-error';
    dateError.className =
      'text-misc-sale ml:text-error text-10 mt-1 validation-error tracking-s';
    dateError.innerHTML = message;

    // Remove any existing date error
    const existingDateError = dateContainer.querySelector('#send-on-error');
    if (existingDateError) {
      existingDateError.remove();
    }

    input.after(dateError);
  }
};

// Validation functions for different field types
const validationFunctions = {
  // Validates recipient email
  recipientEmail: (input) => {
    const { value } = input;
    const isValid = value && value.trim() !== '' && validateEmail(value);

    if (!isValid) {
      const emailError = document.getElementById('email-error');
      if (emailError) {
        emailError.innerHTML =
          window.TMW.Strings.giftCard.recipient.emailRequired ||
          'Valid recipient email is required';
        emailError.style.display = 'block';
      }
      input.classList.add('border-misc-sale', 'ml:border-error');
    }

    // Also check if emails match when validating email field
    const confirmInput = document.getElementById('Recipient-confirm-email');
    if (
      isValid &&
      confirmInput &&
      confirmInput.value &&
      confirmInput.value !== ''
    ) {
      validationFunctions.emailsMatch(input, confirmInput);
    }

    return isValid;
  },

  // Validates confirm email
  recipientConfirmEmail: (input) => {
    const { value } = input;
    const isValid = value && value.trim() !== '';

    if (!isValid) {
      const errorElement = document.getElementById('confirm-email-error');
      if (errorElement) {
        errorElement.innerHTML =
          window.TMW.Strings.giftCard.recipient.confirmEmailRequired ||
          'Please confirm the recipient email';
        errorElement.style.display = 'block';
      }
      input.classList.add('border-misc-sale', 'ml:border-error');
      return false;
    }

    // Check if emails match
    const emailInput = document.getElementById('Recipient-email');
    return validationFunctions.emailsMatch(emailInput, input);
  },

  // Validates that emails match
  emailsMatch: (emailInput, confirmInput) => {
    if (!emailInput || !confirmInput) return true;

    const email = emailInput.value;
    const confirmEmail = confirmInput.value;
    const isValid = email === confirmEmail;

    if (!isValid) {
      const errorElement = document.getElementById('confirm-email-error');
      if (errorElement) {
        errorElement.innerHTML =
          window.TMW.Strings.giftCard.recipient.emailMismatchError ||
          'Email addresses do not match';
        errorElement.style.display = 'block';
      }
      emailInput.classList.add('border-misc-sale', 'ml:border-error');
      confirmInput.classList.add('border-misc-sale', 'ml:border-error');
    }

    return isValid;
  },

  // Validates send on date
  recipientDate: (input) => {
    const isSendImmediately =
      document.getElementById('send-immediately')?.checked;
    if (isSendImmediately) return true;

    const { value } = input;
    const isValid = value && value.trim() !== '';

    // Find the parent .field__input container
    const fieldInputContainer = input.closest('.field__input');

    if (isValid) {
      // Additional validation for date range
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day

      const maxDate = new Date(today);
      maxDate.setDate(today.getDate() + 90);

      if (selectedDate < today) {
        // Date is in the past
        if (fieldInputContainer) {
          fieldInputContainer.classList.add(
            'border-misc-sale',
            'ml:border-error',
          );
        }
        showDateError(window.TMW.Strings.giftCard.recipient.dateFutureError);
        return false;
      }

      if (selectedDate > maxDate) {
        // Date is more than 90 days in the future
        if (fieldInputContainer) {
          fieldInputContainer.classList.add(
            'border-misc-sale',
            'ml:border-error',
          );
        }
        showDateError(window.TMW.Strings.giftCard.recipient.dateRangeError);
        return false;
      }

      return true;
    }

    // Existing validation for empty date
    if (fieldInputContainer) {
      fieldInputContainer.classList.add('border-misc-sale', 'ml:border-error');
    }
    showDateError(window.TMW.Strings.giftCard.recipient.dateRequired);
    return false;
  },
};

// Clear error state for a field
const clearFieldError = (input) => {
  if (!input) return;

  // For date input, clear error from parent container
  if (input.id === 'Recipient-send-on') {
    const fieldInputContainer = input.closest('.field__input');
    if (fieldInputContainer) {
      fieldInputContainer.classList.remove(
        'border-misc-sale',
        'ml:border-error',
      );
    }
  } else {
    // For other inputs, clear error from the input itself
    input.classList.remove('border-misc-sale', 'ml:border-error');
  }

  // Find the error element based on the input id
  const fieldName = input.id.replace('Recipient-', '');
  const errorId = `${fieldName}-error`;
  const errorElement = document.getElementById(errorId);

  if (errorElement) {
    errorElement.style.display = 'none';
  }
};

// Validates the entire gift form
const validateForm = (form) => {
  if (!form) return true;

  // Clear all errors first
  const inputs = form.querySelectorAll('input, textarea');
  inputs.forEach(clearFieldError);

  // Also clear any other error messages
  const errorElements = form.querySelectorAll('.validation-error');
  errorElements?.forEach((el) => {
    const element = el;
    element.style.display = 'none';
  });

  // Clear email error which doesn't have validation-error class
  const emailError = document.getElementById('email-error');
  if (emailError) {
    emailError.style.display = 'none';
  }

  // Find all fields with data-validation attribute and validate them
  const fieldsToValidate = form.querySelectorAll('[data-validation]');
  let isValid = true;

  fieldsToValidate.forEach((field) => {
    const validationMethod = field.dataset.validation;
    if (validationFunctions[validationMethod]) {
      const fieldValid = validationFunctions[validationMethod](field);
      if (!fieldValid) {
        isValid = false;
      }
    }
  });

  return isValid;
};

const AddToCartButton = ({
  isSticky,
  styleNumber,
  currentVariant,
  currentProduct,
  form,
  isQuickView,
  isLookOverlay,
  variantIsAvailable,
  showGiftCardRecipient,
  template,
  giftCardDeliveryTiming,
}) => {
  const [isSendAsGift, setIsSendAsGift] = useState(true);
  const [charCount, setCharCount] = useState(0);
  const isGiftCard = template === 'product.gift-card';

  const inlineDiscount = useDiscount(
    currentProduct.id,
    currentProduct.priceRange.minVariantPrice.amount,
    currentProduct.collectionsId,
  );

  if (window.emailBrRecommendationId !== '0') {
    const prs = productBrRecommendations();
    if (prs !== null) {
      const updateCartAttributes = new CustomEvent(
        'cart:updateCartAttributes',
        {
          detail: {
            payload: {
              attributes: {
                recommendation_urls: prs[0],
                recommendation_images: prs[1],
              },
            },
          },
        },
      );
      document.dispatchEvent(updateCartAttributes);
    }
  }

  const handleAddToCart = async (e) => {
    e.preventDefault();

    const submitForm = e?.target?.form;

    // Validate gift card recipient fields if "Send as a Gift" is checked
    if (isSendAsGift) {
      // Validate all fields using the centralized validation function
      const isValid = validateForm(submitForm);

      // Stop form submission if validation fails
      if (!isValid) {
        return;
      }
    }

    if (!currentVariant && !isQuickView) {
      form.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }

    let label = setProductCartLabel(currentProduct.tags || []);
    if (label) {
      label = label.trim().replace(/\s+/g, ' ');
    }

    if (styleNumber != null) {
      const policyData = ProductPolicy(
        currentProduct,
        currentVariant,
        inlineDiscount,
      );
      const { policyPopup, currentVariantId } = policyData;

      if (policyData.enabled) {
        const frTitle = currentProduct?.fr_title?.value
          ? currentProduct.fr_title?.value
          : currentProduct.fr_title;
        const frColor = currentProduct?.fr_color?.value
          ? currentProduct.fr_color?.value
          : currentProduct.fr_color;
        const cartLabel = label;
        if (typeof window.brPolicyInit === 'function') {
          const showPopup = await window.brPolicyInit(
            currentVariantId,
            frTitle,
            frColor,
            policyPopup,
            cartLabel,
          );
          if (showPopup) {
            return;
          }
        }
      }
    }

    const attributes = [
      {
        key: '_FR_Title',
        value: currentProduct?.fr_title?.value
          ? currentProduct.fr_title?.value
          : currentProduct.fr_title,
      },
      {
        key: '_FR_Color',
        value: currentProduct?.fr_color?.value
          ? currentProduct.fr_color?.value
          : currentProduct.fr_color,
      },
      {
        key: '_cart_label',
        value: label,
      },
    ];

    if (showGiftCardRecipient && isSendAsGift) {
      // Get values here since we've already validated them
      const gcName = submitForm?.querySelector(
        'input[name="properties[Recipient name]"]',
      )?.value;
      const gcEmail = submitForm?.querySelector(
        'input[name="properties[Recipient email]"]',
      )?.value;
      const gcMessage = submitForm?.querySelector(
        'textarea[name="properties[Message]"]',
      )?.value;
      const gcSendOn = submitForm?.querySelector(
        'input[name="properties[Send on]"]',
      )?.value;

      attributes.push({ key: 'Recipient name', value: gcName });
      attributes.push({ key: 'Recipient email', value: gcEmail });
      attributes.push({ key: 'Message', value: gcMessage });
      attributes.push({ key: 'Send on', value: gcSendOn });
      attributes.push({
        key: '__shopify_send_gift_card_to_recipient',
        value: 'on',
      });
    }

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          merchandiseId: currentVariant.id,
          quantity: 1,
          attributes,
        },
      },
    });
    document.dispatchEvent(addToCartEvent);
  };

  const discount = useDiscount(
    currentProduct.id,
    currentVariant?.price?.amount,
    currentProduct.collectionsId,
  );

  const currentProductComingSoon =
    currentProduct.tags.filter((tag) => tag.toLowerCase() === 'coming-soon')
      .length > 0;

  return (
    <div
      className={clsx(
        'atc-container pt-16 pb-40 gap-16 flex flex-col lc:lg:py-20',
        {
          'absolute px-24 bottom-0 w-full h-[115px] ml:h-[117px] bg-white lc:border-t lc:border-t-grey-200 ml:shadow-[0_16px_34px_0px_rgba(0,0,0,0.25)] md:w-[507px] z-20':
            isQuickView,
          'ml:pt-20 ml:md:py-22 gap-24 ml:tablet:gap-22 lc:pb-0 lc:lg:pb-0 ml:pb-0 ml:lg:pb-0 lc:md:pt-24 lc:lg:pt-24 ml:md:pt-24':
            isLookOverlay,
        },
      )}
    >
      {showGiftCardRecipient && !isSticky ? (
        <div
          className={clsx('grid gap-16 mb-16', {
            'ml:text-center': isGiftCard,
          })}
        >
          <div
            className={clsx('flex items-center mb-6', {
              'ml:justify-center': isGiftCard,
            })}
          >
            <input
              type="checkbox"
              id="send-as-gift"
              className="lc:rounded !border-black"
              checked={isSendAsGift}
              onChange={() => setIsSendAsGift(!isSendAsGift)}
            />
            <label htmlFor="send-as-gift" className="ml-3 !text-14 font-normal">
              {window.TMW.Strings.giftCard.recipient.sendAsGift}
            </label>
          </div>
          {isSendAsGift ? (
            <div className="grid gap-16">
              <div className="grid">
                <label className="sr-only" htmlFor="Recipient-email">
                  {window.TMW.Strings.giftCard.recipient.emailLabel}
                </label>
                <input
                  className={clsx(
                    'field__input lc:rounded-4 border border-gray-300 px-8 py-6 text-14 placeholder-gray-500 tracking-s',
                    {
                      'ml:text-center': isGiftCard,
                    },
                  )}
                  id="Recipient-email"
                  type="email"
                  name="properties[Recipient email]"
                  placeholder={
                    window.TMW.Strings.giftCard.recipient.emailPlaceholder
                  }
                  data-validation="recipientEmail"
                  onInput={() => {
                    const formElement = document.querySelector(
                      'form[data-product-form]',
                    );
                    if (formElement) validateForm(formElement);
                  }}
                />
                <div
                  id="email-error"
                  className="text-misc-sale ml:text-error text-10 mt-1 tracking-s"
                  style={{ display: 'none' }}
                ></div>
              </div>
              <div className="grid">
                <label className="sr-only" htmlFor="Recipient-confirm-email">
                  {window.TMW.Strings.giftCard.recipient.confirmEmailLabel}
                </label>
                <input
                  className={clsx(
                    'field__input lc:rounded-4 border border-gray-300 px-8 py-6 text-14 placeholder-gray-500 tracking-s',
                    {
                      'ml:text-center': isGiftCard,
                    },
                  )}
                  id="Recipient-confirm-email"
                  type="email"
                  name="properties[Recipient confirm email]"
                  placeholder={
                    window.TMW.Strings.giftCard.recipient
                      .confirmEmailPlaceholder
                  }
                  data-validation="recipientConfirmEmail"
                  onInput={() => {
                    const formElement = document.querySelector(
                      'form[data-product-form]',
                    );
                    if (formElement) validateForm(formElement);
                  }}
                />
                <div
                  id="confirm-email-error"
                  className="text-misc-sale ml:text-error text-10 mt-1 tracking-s"
                  style={{ display: 'none' }}
                ></div>
              </div>
              <div className="grid">
                <label className="sr-only" htmlFor="Recipient-name">
                  {window.TMW.Strings.giftCard.recipient.nameLabel}
                </label>
                <input
                  className={clsx(
                    'field__input lc:rounded-4 border border-gray-300 px-8 py-6 text-14 placeholder-gray-500 tracking-s',
                    {
                      'ml:text-center': isGiftCard,
                    },
                  )}
                  autoComplete="name"
                  type="text"
                  id="Recipient-name"
                  name="properties[Recipient name]"
                  placeholder={
                    window.TMW.Strings.giftCard.recipient.namePlaceholder
                  }
                />
              </div>
              <div className="grid">
                <label className="sr-only" htmlFor="Recipient-message">
                  {window.TMW.Strings.giftCard.recipient.messageLabel}
                </label>
                <div>
                  <textarea
                    rows="6"
                    id="Recipient-message"
                    className={clsx(
                      'text-area field__input lc:rounded-4 border border-gray-300 px-8 py-3 text-14 placeholder-gray-500 w-full tracking-s',
                      {
                        'ml:text-center': isGiftCard,
                      },
                    )}
                    name="properties[Message]"
                    maxLength="200"
                    placeholder={
                      window.TMW.Strings.giftCard.recipient.messagePlaceholder
                    }
                    onInput={(e) => {
                      const currentLength = e.target.value.length;
                      setCharCount(currentLength);

                      const formElement = document.querySelector(
                        'form[data-product-form]',
                      );
                      if (formElement) validateForm(formElement);
                    }}
                  ></textarea>
                  <div className="text-10 text-subdued mt-2 lc:text-right ml:text-center tracking-s">
                    {window.TMW.Strings.giftCard.recipient.characterCount
                      .replace('{{current}}', charCount)
                      .replace('{{max}}', '200')}
                  </div>
                </div>
              </div>
              <div className="grid">
                <label className="sr-only" htmlFor="Recipient-send-on">
                  {window.TMW.Strings.giftCard.recipient.sendOnLabel}
                </label>
                <div
                  className={clsx(
                    'mb-12 text-12 text-gray-500 font-normal tracking-s',
                    {
                      'ml:text-center': isGiftCard,
                    },
                  )}
                >
                  {window.TMW.Strings.giftCard.recipient.sendOnLabel}
                </div>
                <div className="grid">
                  <div
                    className={clsx('flex gap-15', {
                      'ml:justify-center': isGiftCard,
                      'lc:justify-left': isGiftCard,
                    })}
                  >
                    <button
                      type="button"
                      id="send-immediately-btn"
                      className={`text-14 py-8 px-8 border border-gray-300 
                        lc:rounded-4 flex-grow md:flex-none tracking-s ${
                          Shopify.locale === 'fr'
                            ? 'md:w-[182px]'
                            : 'md:w-[120px]'
                        } md:h-[40px] text-center bg-black 
                      text-white font-normal`}
                      onClick={() => {
                        const dateInput =
                          document.getElementById('Recipient-send-on');
                        const dateContainer =
                          document.getElementById('date-container');
                        const selectDateBtn =
                          document.getElementById('select-date-btn');
                        const immediatelyBtn = document.getElementById(
                          'send-immediately-btn',
                        );

                        // Hide date picker and set to today's date
                        dateContainer.style.display = 'none';
                        selectDateBtn.classList.remove(
                          'bg-black',
                          'text-white',
                        );
                        immediatelyBtn.classList.add('bg-black', 'text-white');

                        // Set to today's date
                        const today = new Date();
                        const year = today.getFullYear();
                        const month = String(today.getMonth() + 1).padStart(
                          2,
                          '0',
                        );
                        const day = String(today.getDate()).padStart(2, '0');
                        const formattedDate = `${year}-${month}-${day}`;

                        dateInput.value = formattedDate;

                        // Set hidden checkbox for form submission
                        document.getElementById(
                          'send-immediately',
                        ).checked = true;
                      }}
                    >
                      {window.TMW.Strings.giftCard.recipient.immediatelyLabel}
                    </button>
                    <button
                      type="button"
                      id="select-date-btn"
                      className={`text-14 py-8 px-8 border border-gray-300 
                        lc:rounded-4 flex-grow md:flex-none tracking-s ${
                          Shopify.locale === 'fr'
                            ? 'md:w-[182px]'
                            : 'md:w-[120px]'
                        } md:h-[40px] text-center font-normal`}
                      onClick={() => {
                        const dateContainer =
                          document.getElementById('date-container');
                        const selectDateBtn =
                          document.getElementById('select-date-btn');
                        const immediatelyBtn = document.getElementById(
                          'send-immediately-btn',
                        );

                        // Show date picker
                        dateContainer.style.display = 'block';
                        immediatelyBtn.classList.remove(
                          'bg-black',
                          'text-white',
                        );
                        selectDateBtn.classList.add('bg-black', 'text-white');

                        // Set hidden checkbox for form submission
                        document.getElementById(
                          'send-immediately',
                        ).checked = false;
                      }}
                    >
                      {window.TMW.Strings.giftCard.recipient.selectDateLabel}
                    </button>
                  </div>
                  <input
                    type="checkbox"
                    id="send-immediately"
                    className="hidden"
                    defaultChecked={true}
                  />
                </div>
                <div id="date-container" className="hidden mt-16">
                  <div className="relative mb-8">
                    <div className="field__input lc:rounded-4 border border-gray-300 px-8 py-10 w-full flex flex-col">
                      <div className="text-12 text-gray-500 font-normal mb-4 tracking-s">
                        {
                          window.TMW.Strings.giftCard.recipient
                            .scheduleWhenToSend
                        }
                      </div>
                      <input
                        className="text-14 lc:text-left ml:text-center leading-100 bg-transparent border-none p-0 focus:outline-none w-full tracking-s lc:[&::-webkit-date-and-time-value]:text-left"
                        autoComplete="send_on"
                        type="date"
                        id="Recipient-send-on"
                        name="properties[Send on]"
                        pattern="\d{4}-\d{2}-\d{2}"
                        data-validation="recipientDate"
                        min={(() => {
                          const today = new Date();
                          return today.toISOString().split('T')[0];
                        })()}
                        max={(() => {
                          const today = new Date();
                          const maxDate = new Date(today);
                          maxDate.setDate(today.getDate() + 90);
                          return maxDate.toISOString().split('T')[0];
                        })()}
                        onInput={() => {
                          const formElement = document.querySelector(
                            'form[data-product-form]',
                          );
                          if (formElement) validateForm(formElement);
                        }}
                        ref={(el) => {
                          if (el) {
                            // Use a static variable to track initialization
                            if (!AddToCartButton.dateInitialized) {
                              // Set today's date as default only once
                              const today = new Date();
                              const year = today.getFullYear();
                              const month = String(
                                today.getMonth() + 1,
                              ).padStart(2, '0');
                              const day = String(today.getDate()).padStart(
                                2,
                                '0',
                              );
                              // Set the value without modifying the parameter directly
                              const dateValue = `${year}-${month}-${day}`;
                              const element = el; // Create a local variable
                              element.value = dateValue;
                              // Use a static property instead
                              AddToCartButton.dateInitialized = true;
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div
                    className="text-10 text-subdued text-center mb-4 leading-160 tracking-[1px] [&_p]:text-10 [&_span]:text-10 [&_*]:text-10 [&_*]:text-subdued [&_*]:leading-160 [&_*]:tracking-[1px]"
                    dangerouslySetInnerHTML={{
                      __html:
                        giftCardDeliveryTiming ||
                        window.TMW.Strings.giftCard.recipient.deliveryTiming,
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <button
        type="submit"
        id="add-to-cart-cta"
        className={`
          button button--primary
          w-full
          group
          ml:uppercase
          ${
            currentVariant || !currentProduct.availableForSale
              ? ''
              : 'hover:bg-black hover:text-white'
          }`}
        disabled={
          (currentVariant && variantIsAvailable === false) ||
          !currentProduct.availableForSale
        }
        onClick={(e) => handleAddToCart(e)}
      >
        {currentVariant ? (
          variantIsAvailable ? (
            `${window.TMW.Strings.product.addToCart} - ${formatMoney(
              discount?.discountAmount > 0
                ? discount.discountPrice
                : currentVariant.price,
              false,
            )}`
          ) : currentProductComingSoon ? (
            window.TMW.Strings.product.comingSoon
          ) : variantIsAvailable === false ? (
            window.TMW.Strings.product.outOfStock
          ) : (
            window.TMW.Strings.product.addToCart
          )
        ) : (
          <>
            {currentProduct.availableForSale ? (
              <>
                <div className="group-hover:hidden">
                  {window.TMW.Strings.product.addToCart}
                </div>
                <div className="hidden group-hover:block">
                  {window.TMW.Strings.product.selectSize}
                </div>
              </>
            ) : currentProductComingSoon ? (
              window.TMW.Strings.product.comingSoon
            ) : variantIsAvailable === false ||
              !currentProduct.availableForSale ? (
              window.TMW.Strings.product.outOfStock
            ) : (
              window.TMW.Strings.product.addToCart
            )}
          </>
        )}
      </button>

      <div
        className={clsx('flex justify-center', {
          'items-center gap-40': isQuickView,
          hidden: isGiftCard, // Hide wishlist button on gift card pages
        })}
      >
        <button
          type="button"
          className={clsx(
            'swym-button swym-add-to-wishlist-view-product !block pointer-events-none [&.swym-loaded]:!pointer-events-auto group !h-auto !p-0 !normal-case [&.disabled]:!opacity-100 [&>span]:!body-3-underline  [&:hover>span]:!no-underline flex-none !leading-100',
            `product_${decodeId(currentProduct.id, 'Product')}`,
          )}
          data-with-epi="true"
          data-swaction="addToWishlist"
          data-product-id={decodeId(currentProduct.id, 'Product')}
          data-variant-id={decodeId(
            currentProduct?.variants?.[0].id,
            'ProductVariant',
          )}
          data-product-url={generateUrl(currentProduct.handle, 'products')}
        >
          <span className="!text-default group-[.swym-added]:hidden">
            {window.TMW.Strings.product.addToWishlist}
          </span>
          <span className="!text-default hidden group-[.swym-added]:!block">
            {window.TMW.Strings.product.addedToWishlist}
          </span>
        </button>
      </div>
    </div>
  );
};

const ProductForm = ({
  currentProduct: product,
  currentVariant,
  products,
  relatedProducts,
  onProductChange,
  setCurrentVariant,
  isQuickView = false,
  isLookOverlay = false,
  galleryInView,
  options,
  setOptions,
  showGiftCardRecipient,
  template,
  data,
}) => {
  const giftCardDeliveryTiming = data?.giftCardDeliveryTiming;
  const { masterSKU } = product;
  const [formContainer, formInView] = useInView();
  const [footerInView, setFooterInView] = useState(false);
  const [variantIsAvailable, setVariantIsAvailable] = useState();

  const form = useRef(null);
  const inputPrefix = useUniqueId();
  const { isMobile } = useResponsive();
  const hasOnlyDefaultVariant = useMemo(
    () =>
      product.hasOnlyDefaultVariant ||
      (product.options &&
        product.options.every((option) => option.values?.length === 1)),
    [product],
  );

  const productHasFit = useMemo(
    () =>
      product.options &&
      product.options.find(
        (option) =>
          option.name.trim().toLowerCase() ===
          window.TMW.Strings.product.options.fit,
      ) &&
      product.options
        .find(
          (option) =>
            option.name.trim().toLowerCase() ===
            window.TMW.Strings.product.options.fit,
        )
        .values?.find((value) => value !== 'Melanie Lyne'),
    [product.options],
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    let currentVariantId = 0;
    if (currentVariant) {
      const id = currentVariant?.id.split('/ProductVariant/')[1];
      currentVariantId = id;
      searchParams.set('variant', id);
    }
    if (!isLookOverlay && !isQuickView) {
      const newRelativePathQuery = `${Shopify.routes.root}products/${
        product.handle
      }?${searchParams.toString()}${window.location.hash}`;
      window.history.replaceState(null, '', newRelativePathQuery);
    }

    if (currentVariantId !== 0) {
      if (!product.useTheRegularInventory?.value) {
        middleware.getVariantInventory(currentVariantId).then((isAvailable) => {
          setVariantIsAvailable(isAvailable);
        });
      } else {
        setVariantIsAvailable(currentVariant.availableForSale);
      }
    }
  }, [currentVariant, product]);

  useEffect(() => {
    // Check if this is a gift card template
    const isGiftCard = template === 'product.gift_card';

    // For gift cards, don't pre-select a variant
    if (isGiftCard) {
      setCurrentVariant(null);
      return;
    }

    if (!hasOnlyDefaultVariant) {
      const currentOptions = options.reduce(
        (items, item) => ({
          ...items,
          ...{
            [item.name]: item.value,
          },
        }),
        {},
      );
      const newVariant = product.variants.find((v) =>
        v.selectedOptions.every(
          (opt) => currentOptions[opt.name] === opt.value,
        ),
      );
      setCurrentVariant(newVariant || null);
      if (newVariant && !isQuickView && !isLookOverlay) {
        document.dispatchEvent(
          new CustomEvent('productUpdated', {
            detail: product,
          }),
        );
      }
    } else {
      setCurrentVariant({
        ...product.variants[0],
      });
    }
  }, [options]);

  const isStickyVisible = useMemo(() => {
    if (isMobile) {
      return !formInView && !galleryInView && !footerInView;
    }
    return !formInView && !footerInView;
  }, [formInView, galleryInView, footerInView, isMobile]);

  const variantSelectors = useMemo(
    () =>
      !product.hasOnlyDefaultVariant && (
        <div
          className={
            isQuickView
              ? 'px-24 overflow-auto hide-scrollbar'
              : isLookOverlay
              ? 'flex flex-col items-end lc:mb-8 ml:mb-4 lc:md:mb-0 ml:md:mb-0 md:min-h-20 lc:md:min-h-[75px]'
              : ''
          }
        >
          {product.options.map((option) => (
            <VariantSelector
              key={option.name}
              inputPrefix={inputPrefix}
              option={option}
              product={product}
              relatedProducts={relatedProducts}
              isQuickView={isQuickView}
              isLookOverlay={isLookOverlay}
              options={options}
              setOptions={setOptions}
              useTheRegularInventory={product.useTheRegularInventory?.value}
              template={template}
            />
          ))}
        </div>
      ),
    [product, options, relatedProducts],
  );

  const price = useMemo(() => {
    // Check if this is a gift card template
    const isGiftCard = template === 'product.gift_card';
    // Only show range if it's a gift card AND no variant is selected
    const showRange = isGiftCard && !currentVariant;

    return (
      <ProductPrice
        className={clsx(
          '[&_.price]:body-1 ml:text-center flex flex-wrap gap-x-4 items-center',
          {
            'ml:justify-center': !isLookOverlay,
          },
        )}
        saleClass="text-misc-sale body-3"
        compareClass="body-3 text-subdued"
        showRange={showRange}
        showLabel={true}
        variant={currentVariant}
        product={product}
      />
    );
  }, [currentVariant, product, template]);

  const addToCartButton = useCallback(
    (isSticky) => (
      <AddToCartButton
        styleNumber={masterSKU?.value}
        currentVariant={currentVariant}
        currentProduct={product}
        form={form}
        isQuickView={isQuickView}
        isLookOverlay={isLookOverlay}
        variantIsAvailable={variantIsAvailable}
        showGiftCardRecipient={showGiftCardRecipient}
        isSticky={isSticky}
        template={template}
        giftCardDeliveryTiming={giftCardDeliveryTiming}
      />
    ),
    [
      currentVariant,
      form,
      product,
      variantIsAvailable,
      showGiftCardRecipient,
      template,
      giftCardDeliveryTiming,
    ],
  );

  // used for hiding sticky ATC when footer is visible
  const intersectionObserver = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      setFooterInView(true);
    } else {
      setFooterInView(false);
    }
  });
  const footer = document.querySelector('footer');
  intersectionObserver.observe(footer);

  const StyleNumberLg = useMemo(() => (
    <ProductStyleNumber
      className={clsx(
        'body-3 ml:body-6 lc:text-subdued mt-8 sm:hidden md:block styleNumber_ML_CSS below-374:hidden',
        { '!hidden': template === 'product.gift-card' },
      )}
      styleNumber={masterSKU?.value}
    />
  ));

  const StyleNumberSm = useMemo(() => (
    <ProductStyleNumber
      className={clsx(
        'body-3 ml:body-6 lc:text-subdued mt-8 sm:block md:hidden styleNumber_ML_CSS_mob',
        { '!hidden': template === 'product.gift-card' },
      )}
      styleNumber={masterSKU?.value}
    />
  ));

  const StyleNumberViewDetails = useMemo(() => (
    <div
      className={clsx(
        'flex items-center justify-between body-3 ml:body-6 mb-12 md:mb-16 ml:mb-24',
        { '!hidden': template === 'product.gift-card' },
      )}
    >
      <ProductStyleNumber
        styleNumber={masterSKU?.value}
        className="lc:text-subdued"
      />
      <a
        href={`${Shopify.routes.root}products/${product.handle}`}
        className="body-3-underline flex-none"
      >
        {window.TMW.Strings.product.viewDetails}
      </a>
    </div>
  ));

  const policyNotes = useMemo(
    () => (
      <ProductPolicyNotes
        className={clsx('mb-16', 'md:mb-20', 'br-product-policy-title')}
        styleNumber={masterSKU?.value}
        currentProduct={product}
        currentVariant={currentVariant}
      />
    ),
    [currentVariant, form, product],
  );

  const returnPolicyNotes = useMemo(
    () => (
      <ProductReturnPolicyNotes
        className={clsx('mb-16', 'md:mb-20', 'br-product-policy-title')}
        styleNumber={masterSKU?.value}
        currentProduct={product}
        currentVariant={currentVariant}
      />
    ),
    [currentVariant, form, product],
  );

  return (
    <>
      <div
        className={clsx({
          'pb-[115px] ml:pb-[117px]': isQuickView,
          'flex flex-col items-end': isLookOverlay,
        })}
        ref={formContainer}
      >
        <div
          className={clsx('flex flex-col items-end', {
            'lc:min-h-182 ml:min-h-168 lc:md:min-h-[234px] ml:md:min-h-164':
              isLookOverlay && !productHasFit,
            'lc:min-h-182 ml:min-h-168 lc:md:min-h-0 ml:md:min-h-0':
              isLookOverlay && productHasFit,
            'w-full lc:md:max-w-271 ml:md:max-w-332 lc:max-w-[calc(100%_-_138px)] ml:max-w-[calc(100%_-_127px)]':
              isLookOverlay,
          })}
        >
          <div
            className={clsx('w-full', {
              'ml:text-center p-24 sm:pt-16 sm:pb-16 ml:pt-8': isQuickView,
              'ml:text-center pt-24 lg:pt-0 ml:pt-32 ml:lg:pt-0 ml:pb-16 lc:lg:pb-20':
                !isQuickView && !isLookOverlay,
              'ml:pb-16 lc:pb-24': isLookOverlay,
            })}
          >
            {policyNotes}
            {returnPolicyNotes}
            {!isQuickView && StyleNumberSm}
            {isQuickView && StyleNumberViewDetails}
            {isLookOverlay ? (
              <h3 className="!normal-case pb-8">
                <a href={generateUrl(product.handle, 'products')}>
                  {product.title}
                </a>
              </h3>
            ) : (
              <h1
                className={`display-4 !normal-case pb-4 lg:pb-10 ${
                  isQuickView ? 'cursor-pointer text-default' : ''
                }`}
              >
                {isQuickView ? (
                  <a href={generateUrl(product.handle, 'products')}>
                    {product.handle.includes('gift-card')
                      ? window.TMW.Strings.giftCard.title
                      : product.title}
                  </a>
                ) : product.handle.includes('gift-card') ? (
                  window.TMW.Strings.giftCard.title
                ) : (
                  product.title
                )}
              </h1>
            )}
            <div
              className={clsx('flex items-center gap-x-8 flex-wrap', {
                'ml:justify-center': !isLookOverlay,
              })}
            >
              {price}
            </div>
            {!isQuickView && StyleNumberLg}
          </div>
          <ProductSwatches
            currentProduct={product}
            products={products}
            isQuickView={isQuickView}
            isLookOverlay={isLookOverlay}
            onChange={onProductChange}
            options={options}
            template={template}
          />
        </div>
        <form
          method="post"
          action="/cart/add"
          acceptCharset="UTF-8"
          encType="multipart/form-data"
          noValidate="novalidate"
          ref={form}
          data-product-form
          className={clsx({
            '[&_.swym-inject]:hidden swym-wishlist-button-bar ': !isLookOverlay,
            '[&_.swym-inject]:hidden swym-wishlist-button-bar w-full':
              isLookOverlay,
          })}
        >
          {variantSelectors}
          {addToCartButton(false)}
        </form>
      </div>

      {!isQuickView && !isLookOverlay && (
        <ProductStickyButton
          product={product}
          variant={currentVariant}
          options={options}
          isVisible={isStickyVisible}
        >
          {addToCartButton(true)}
        </ProductStickyButton>
      )}
    </>
  );
};
export default ProductForm;
